import { supabase } from './supabase';
import { Database } from './database.types';
import { OrganizationService } from './organization-service';

type TeamInvitation = Database['public']['Tables']['team_invitations']['Row'];
type TeamInvitationInsert = Database['public']['Tables']['team_invitations']['Insert'];
type TeamInvitationUpdate = Database['public']['Tables']['team_invitations']['Update'];

export interface TeamInvitationRequest {
  organizationId: string;
  email: string;
  role: 'admin' | 'editor' | 'viewer';
  permissions?: {
    canAnalyze: boolean;
    canUpload: boolean;
    canInvite: boolean;
    canManageTeam: boolean;
    canManageProjects: boolean;
    canDeleteProjects: boolean;
    canViewReports: boolean;
    canExportData: boolean;
  };
}

export interface TeamInvitationInfo extends TeamInvitation {
  inviter: {
    id: string;
    name: string;
    email: string;
  };
}

export class TeamInvitationService {
  /**
   * Send team invitation to join organization
   */
  static async sendTeamInvitation(request: TeamInvitationRequest): Promise<TeamInvitationInfo> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Check if user has permission to invite
      const { data: currentUser, error: currentUserError } = await supabase
        .from('users')
        .select('team_role, organization_id, name, email')
        .eq('id', user.id)
        .single();

      if (currentUserError || !currentUser) {
        throw new Error('Unable to verify user permissions');
      }

      // If user doesn't have an organization, create one automatically
      let organizationId = currentUser.organization_id;
      let userRole = currentUser.team_role;

      if (!organizationId) {
        console.log('🏢 User has no organization, creating first-time organization for team invitation');
        
        try {
          const organization = await OrganizationService.createFirstTimeOrganization(user.id);
          organizationId = organization.id;
          userRole = 'admin'; // User becomes admin of their new organization
          
          console.log('✅ First-time organization created:', {
            organizationId: organization.id,
            organizationName: organization.name,
            userId: user.id
          });
        } catch (orgError) {
          console.error('Error creating first-time organization:', orgError);
          throw new Error('Failed to create organization. Please try again.');
        }
      }

      // Now check permissions (user should be admin or editor)
      if (userRole !== 'admin' && userRole !== 'editor') {
        throw new Error('Insufficient permissions to send invitations');
      }

      // Skip checking existing users to avoid RLS issues
      // The invitation acceptance process will handle any conflicts
      // if a user tries to accept an invitation but is already in an organization

      // Check for existing invitations (any status) - use the current organization ID
      const { data: existingInvitations, error: existingInvitationError } = await supabase
        .from('team_invitations')
        .select('id, status')
        .eq('organization_id', organizationId) // Use the current organization ID (could be newly created)
        .eq('email', request.email)
        .order('created_at', { ascending: false });

      // Only throw error if there's a real database error, not if no invitation found
      if (existingInvitationError) {
        console.warn('Error checking existing invitation:', existingInvitationError);
      }

      if (existingInvitations && existingInvitations.length > 0) {
        // Check if there's a pending invitation
        const pendingInvitation = existingInvitations.find(inv => inv.status === 'pending');
        if (pendingInvitation) {
          throw new Error('A pending invitation already exists for this email');
        }

        // If there are only revoked/expired/declined invitations, clean them up first
        const nonActiveInvitations = existingInvitations.filter(inv => 
          inv.status === 'revoked' || inv.status === 'expired' || inv.status === 'declined'
        );
        
        if (nonActiveInvitations.length > 0) {
          // Use database function to clean up old invitations with elevated permissions
          const { error: deleteError } = await (supabase as any).rpc('cleanup_old_invitations', {
            email_input: request.email,
            org_id: organizationId // Use current organization ID
          });
          
          if (deleteError) {
            console.warn('Error cleaning up old invitations:', deleteError);
            // Continue anyway - the insert might still work
          } else {
            console.log(`Cleaned up ${nonActiveInvitations.length} old invitations for ${request.email}`);
          }
        }
      }

      // Get default permissions for role
      const defaultPermissions = await this.getDefaultPermissionsForRole(request.role);
      
      // Generate unique invitation token
      const invitationToken = this.generateInvitationToken();
      
      const invitationData: TeamInvitationInsert = {
        organization_id: organizationId, // Use current organization ID (potentially newly created)
        email: request.email,
        role: request.role,
        permissions: request.permissions || defaultPermissions,
        invited_by: user.id,
        invitation_token: invitationToken,
        status: 'pending',
        expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days from now
      };

      const { data: invitation, error } = await supabase
        .from('team_invitations')
        .insert(invitationData)
        .select(`
          *,
          users!team_invitations_invited_by_fkey(id, name, email)
        `)
        .single();

      if (error) {
        throw error;
      }

      // TODO: Send invitation email here
      await this.sendInvitationEmail(invitation);

      return {
        ...invitation,
        inviter: (invitation.users as any)
      };
    } catch (error) {
      console.error('Send team invitation error:', error);
      throw error;
    }
  }

  /**
   * Validate invitation token and return invitation data
   */
  static async validateInvitationToken(token: string): Promise<any> {
    try {
      // Get the invitation data without foreign key joins to avoid RLS issues
      const { data: invitation, error } = await supabase
        .from('team_invitations')
        .select('*')
        .eq('invitation_token', token)
        .maybeSingle();

      if (error) {
        console.error('Error validating invitation token:', error);
        return null;
      }

      if (!invitation) {
        console.log('No invitation found for token:', token);
        return null;
      }

      // Check if invitation has expired
      if (invitation.expires_at && new Date(invitation.expires_at) < new Date()) {
        console.log('Invitation has expired');
        return null;
      }

      // Check if invitation is not pending
      if (invitation.status !== 'pending') {
        console.log('Invitation is not pending, status:', invitation.status);
        return null;
      }

      console.log('✅ Valid invitation found:', {
        email: invitation.email,
        role: invitation.role,
        organizationId: invitation.organization_id,
        invitedBy: invitation.invited_by,
        status: invitation.status,
        expiresAt: invitation.expires_at
      });

      // Return basic invitation data without organization/user names
      // The frontend can handle displaying this without the extra details
      return {
        id: invitation.id,
        email: invitation.email,
        role: invitation.role,
        organizationId: invitation.organization_id,
        invitedBy: invitation.invited_by,
        status: invitation.status,
        expiresAt: invitation.expires_at,
        createdAt: invitation.created_at,
        // Provide fallback display values
        organizationName: 'Organization', // Generic fallback
        inviterName: 'Team Admin', // Generic fallback
        isValid: true
      };

    } catch (error) {
      console.error('Error in validateInvitationToken:', error);
      return null;
    }
  }

  /**
   * Accept team invitation
   */
  static async acceptTeamInvitation(invitationToken: string): Promise<void> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      console.log('🔧 Accepting invitation for user:', user.id, user.email);

      // Find the invitation
      const { data: invitation, error: invitationError } = await supabase
        .from('team_invitations')
        .select('*')
        .eq('invitation_token', invitationToken)
        .eq('status', 'pending')
        .maybeSingle();

      if (invitationError) {
        console.warn('Error finding invitation:', invitationError);
        throw new Error('Invalid or expired invitation');
      }

      if (!invitation) {
        throw new Error('Invalid or expired invitation');
      }

      console.log('✅ Found valid invitation:', {
        email: invitation.email,
        role: invitation.role,
        organizationId: invitation.organization_id,
        expiresAt: invitation.expires_at
      });

      // Check if invitation has expired
      if (new Date(invitation.expires_at!) < new Date()) {
        await supabase
          .from('team_invitations')
          .update({ status: 'expired' })
          .eq('id', invitation.id);
        throw new Error('Invitation has expired');
      }

      // Check if email matches current user
      if (user.email !== invitation.email) {
        throw new Error('Invitation email does not match current user');
      }

      // Get or create user in our users table
      let { data: currentUser, error: currentUserError } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (currentUserError && currentUserError.code !== 'PGRST116') {
        // PGRST116 is "no rows found", which is fine for new users
        throw currentUserError;
      }

      if (!currentUser) {
        console.log('🔧 User not found in users table, creating new user record');
        
        // Get organization owner's subscription plan to inherit
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('created_by')
          .eq('id', invitation.organization_id)
          .single();
        
        let orgOwnerPlan = null;
        if (orgData?.created_by) {
          const { data: ownerData, error: ownerError } = await supabase
            .from('users')
            .select('subscription_plan')
            .eq('id', orgData.created_by)
            .single();
          
          orgOwnerPlan = ownerData?.subscription_plan;
          
          if (ownerError) {
            console.warn('Could not fetch organization owner subscription:', ownerError);
          }
        }
        
        // If we can't get the owner's plan, default to a valid plan to avoid 'pending' state
        if (!orgOwnerPlan || orgOwnerPlan === 'pending' || orgOwnerPlan === 'none') {
          console.log('⚠️ Organization owner has no valid subscription plan, defaulting to free trial');
          orgOwnerPlan = 'free_trial'; // Default to free trial instead of leaving it null/pending
        }
        
        console.log('🔧 Creating invited user record with inherited subscription plan:', orgOwnerPlan);

        // Create new user record with inherited subscription plan
        const { data: newUser, error: createUserError } = await supabase
          .from('users')
          .insert({
            id: user.id,
            email: user.email!,
            name: user.user_metadata?.name || user.user_metadata?.full_name || 'New User',
            organization_id: invitation.organization_id,
            team_role: invitation.role,
            subscription_plan: orgOwnerPlan, // Inherit organization owner's plan
            credits: 0, // No individual credits - will use organization credits
            tutorial_completed: false
          })
          .select()
          .single();

        if (createUserError) {
          console.error('Error creating user:', createUserError);
          throw createUserError;
        }

        currentUser = newUser;
        console.log('✅ Created new user in users table with inherited subscription:', currentUser);
      } else {
        console.log('🔧 Updating existing user with organization and role');
        
        // Get organization owner's subscription plan to inherit
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('created_by')
          .eq('id', invitation.organization_id)
          .single();
        
        let orgOwnerPlan = null;
        if (orgData?.created_by) {
          const { data: ownerData, error: ownerError } = await supabase
            .from('users')
            .select('subscription_plan')
            .eq('id', orgData.created_by)
            .single();
          
          orgOwnerPlan = ownerData?.subscription_plan;
          
          if (ownerError) {
            console.warn('Could not fetch organization owner subscription:', ownerError);
          }
        }
        
        // If we can't get the owner's plan, default to a valid plan to avoid 'pending' state
        if (!orgOwnerPlan || orgOwnerPlan === 'pending' || orgOwnerPlan === 'none') {
          console.log('⚠️ Organization owner has no valid subscription plan, defaulting to free trial');
          orgOwnerPlan = 'free_trial'; // Default to free trial instead of leaving it null/pending
        }
        
        console.log('🔧 Updating user with inherited subscription plan:', orgOwnerPlan);

        // Update existing user's organization, role, and inherit subscription plan
        const { error: updateUserError } = await supabase
          .from('users')
          .update({
            organization_id: invitation.organization_id,
            team_role: invitation.role,
            subscription_plan: orgOwnerPlan, // Inherit organization owner's plan
            credits: 0 // No individual credits - will use organization credits
          })
          .eq('id', user.id);

        if (updateUserError) {
          throw updateUserError;
        }
        console.log('✅ Updated user organization, role, and subscription');
      }

      // Mark invitation as accepted
      const { error: updateInvitationError } = await supabase
        .from('team_invitations')
        .update({ status: 'accepted' })
        .eq('id', invitation.id);

      if (updateInvitationError) {
        throw updateInvitationError;
      }

      console.log('✅ Team invitation accepted successfully', {
        userId: user.id,
        organizationId: invitation.organization_id,
        role: invitation.role
      });

    } catch (error) {
      console.error('Accept team invitation error:', error);
      throw error;
    }
  }

  /**
   * Decline team invitation
   */
  static async declineTeamInvitation(invitationToken: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('team_invitations')
        .update({ status: 'declined' })
        .eq('invitation_token', invitationToken)
        .eq('status', 'pending');

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Decline team invitation error:', error);
      throw error;
    }
  }

  /**
   * Get team invitations for organization
   */
  static async getTeamInvitations(organizationId: string, status?: string): Promise<TeamInvitationInfo[]> {
    try {
      let query = supabase
        .from('team_invitations')
        .select(`
          *,
          users!team_invitations_invited_by_fkey(id, name, email)
        `)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const { data: invitations, error } = await query;

      if (error) {
        throw error;
      }

      return (invitations || []).map(invitation => ({
        ...invitation,
        inviter: (invitation.users as any)
      }));
    } catch (error) {
      console.error('Get team invitations error:', error);
      throw error;
    }
  }

  /**
   * Revoke team invitation
   */
  static async revokeTeamInvitation(invitationId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('team_invitations')
        .update({ status: 'revoked' })
        .eq('id', invitationId)
        .eq('status', 'pending');

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Revoke team invitation error:', error);
      throw error;
    }
  }

  /**
   * Clean up expired invitations
   */
  static async cleanupExpiredInvitations(): Promise<void> {
    try {
      const { error } = await supabase
        .from('team_invitations')
        .update({ status: 'expired' })
        .eq('status', 'pending')
        .lt('expires_at', new Date().toISOString());

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Cleanup expired invitations error:', error);
      throw error;
    }
  }

  /**
   * Get default permissions for role
   */
  private static async getDefaultPermissionsForRole(role: string): Promise<Record<string, boolean>> {
    try {
      const { data: permissions, error } = await supabase
        .from('team_permissions')
        .select('permission, can_perform')
        .eq('role', role);

      if (error) {
        throw error;
      }

      const permissionMap: Record<string, boolean> = {};
      permissions?.forEach(perm => {
        permissionMap[perm.permission] = perm.can_perform ?? false;
      });

      return permissionMap;
    } catch (error) {
      console.error('Get default permissions error:', error);
      // Return fallback permissions
      return {
        canAnalyze: role !== 'viewer',
        canUpload: role !== 'viewer',
        canInvite: role === 'admin',
        canManageTeam: role === 'admin',
        canManageProjects: role !== 'viewer',
        canDeleteProjects: role === 'admin',
        canViewReports: true,
        canExportData: role !== 'viewer'
      };
    }
  }

  /**
   * Send invitation email using Supabase Edge Function
   */
  private static async sendInvitationEmail(invitation: any): Promise<void> {
    try {
      // Call the Edge Function to send invitation email
      const { data, error } = await supabase.functions.invoke('send-team-invitation', {
        body: {
          email: invitation.email,
          role: invitation.role,
          organizationId: invitation.organization_id,
          invitationId: invitation.id,
          invitationToken: invitation.invitation_token,
          inviterName: invitation.users?.name || 'Team Admin'
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error(`Failed to send invitation email: ${error.message}`);
      }

      if (!data?.success) {
        console.error('Edge function failed:', data);
        throw new Error(data?.error || 'Failed to send invitation email');
      }

      console.log(`✅ Successfully sent invitation email to ${invitation.email} for role ${invitation.role}`);
      console.log('Edge function response:', data);
      
    } catch (error) {
      console.error('Send invitation email error:', error);
      throw error;
    }
  }

  /**
   * Generate unique invitation token
   */
  private static generateInvitationToken(): string {
    // Generate a secure random token
    const timestamp = Date.now().toString();
    const randomBytes = crypto.getRandomValues(new Uint8Array(16));
    const randomString = Array.from(randomBytes, byte => byte.toString(16).padStart(2, '0')).join('');
    return `inv_${timestamp}_${randomString}`;
  }
} 